// SOFTPHONE-1 (2026-09-14) — the browser's own comms session, and the Twilio
// Device that hangs off it.
//
// COMMS-RETIRE-1 deleted the old browser dialler for a reason that no longer
// applies: it registered against a TwiML App on the CRM's *own* Twilio project,
// and that project owns no numbers any more. This one registers against the
// comms platform's Twilio account, using the same `twilio_identity` the SOLCOM
// app registers with — which is the whole trick. Twilio forks a single
// `client:<identity>` leg to every endpoint registered for that identity, so
// the desk and the handset ring together and whichever answers first wins.
// (Verified on real devices, 14 Sep 2026: one leg, browser over websocket and
// iPhone over PushKit, both notified, the loser gets `cancel`.)
//
// Why the ticket dance instead of just asking the CRM for a voice token:
// comms mints Voice SDK tokens only for a signed-in user, never for a service
// key, because a key that could would let its holder register as anyone and
// take their calls. So the CRM hands the browser a single-use, ~30-second
// ticket naming the signed-in user; the browser swaps it for an ordinary comms
// session and mints its own token. See comms-api docs/CRM-INTEGRATION.md,
// "Browser session hand-off".
//
// Nothing here is persisted. The session lives in memory for the life of the
// tab: a reload just does the exchange again, which is cheap, and a comms
// refresh token in localStorage would be a standing credential on a shared
// desktop for no benefit.

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Renew the comms access token this long before it actually expires. */
const ACCESS_RENEW_MARGIN_S = 60;

async function commsFetch(baseUrl, path, { token, body } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${baseUrl}/v1${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const err = await res.json();
      detail = err?.error?.code ? `${err.error.code}: ${err.error.message}` : detail;
    } catch {
      /* non-JSON error body; the status is all we have */
    }
    const e = new Error(`comms ${path} failed — ${detail}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

/**
 * Ask the CRM for a ticket and immediately spend it on a comms session.
 *
 * The ticket is deliberately short-lived, so this is one continuous operation:
 * we never hold an unspent ticket, and it never touches storage.
 */
export async function openCommsSession() {
  const { data } = await axios.post(`${API}/comms/voice/softphone-session`);
  if (!data?.ticket || !data?.base_url) throw new Error("comms did not return a ticket");

  const session = await commsFetch(data.base_url, "/session/redeem", { body: { ticket: data.ticket } });
  return {
    baseUrl: data.base_url,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    tenantId: session.tenant?.id,
    membership: session.membership,
    // `expires_in` is seconds from now; keep the absolute moment instead so a
    // long-idle tab doesn't reason from a stale duration.
    accessExpiresAt: Date.now() + (Number(session.expires_in) || 900) * 1000,
  };
}

/**
 * Renew the comms access token in place, using the refresh token.
 *
 * `switch-tenant` back to the tenant we are already in is how the SOLCOM app
 * silently renews too — it is not a switch, it is the only refresh endpoint
 * there is (comms-api CLAUDE.md, "Auth — two schemes").
 *
 * SCRM-23 / SC-12 (30 Oct 2026): we ask comms to rotate the refresh token on
 * every renewal and keep the new one. The old one stays valid for 30 seconds
 * (two renewals racing don't sign anyone out); presenting it later is treated
 * as theft and ends the whole sign-in. The session object (held in
 * CallContext's sessionRef, in memory only) is the one place it lives, so the
 * returned session must replace the old one. If comms ever answers without a
 * new token, the current one is kept.
 */
export async function renewCommsSession(session) {
  const renewed = await commsFetch(session.baseUrl, "/session/switch-tenant", {
    body: { refresh_token: session.refreshToken, tenant_id: session.tenantId, rotate: true },
  });
  return {
    ...session,
    accessToken: renewed.access_token,
    refreshToken: renewed.refresh_token || session.refreshToken,
    accessExpiresAt: Date.now() + (Number(renewed.expires_in) || 900) * 1000,
  };
}

/** A comms session whose access token is good for at least the next minute. */
export async function freshSession(session) {
  if (Date.now() < session.accessExpiresAt - ACCESS_RENEW_MARGIN_S * 1000) return session;
  return renewCommsSession(session);
}

/**
 * Mint a Twilio Voice access token for this session's membership.
 *
 * Returns the token and the (possibly renewed) session, so callers thread the
 * session through rather than holding a stale one.
 */
export async function mintVoiceToken(session) {
  const live = await freshSession(session);
  const token = await commsFetch(live.baseUrl, "/session/twilio-token", { token: live.accessToken });
  return { token: token.token, identity: token.identity, session: live };
}

/**
 * Load the Twilio Voice SDK on demand.
 *
 * Deliberately dynamic: the SDK is ~300KB and only brokers with a comms
 * membership ever register a device, so it stays out of the initial bundle.
 */
export async function loadVoiceSdk() {
  const mod = await import("@twilio/voice-sdk");
  return mod.Device;
}
